/**
 * The doc page's editor (slice 2d spec §10.3; boards 48b, 48c, 48g, 51e): the
 * title, Write and Preview, a counter to 200,000, Save and Discard. It runs
 * on the Memory page's draft (`app/draft.ts`): a `doc` event while the draft
 * has changes waits behind "Reload", and a Save from a stale version comes
 * back `409 conflict` with their version, to use theirs or keep mine (which
 * saves over theirs, and can't be put back: there's no history, decision 10).
 * New doc's editor is the same, and its first Save makes the doc.
 */
import { Button } from "@crew/ui/components/button";
import { FieldError } from "@crew/ui/components/field";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { Textarea } from "@crew/ui/components/textarea";
import { toneText } from "@crew/ui/components/typography";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { CircleAlert, RefreshCw } from "lucide-react";
import {
  useDeferredValue,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { refusalOf, useCreateDoc, useSaveDoc, type Doc } from "./api";
import { brokenBlocks, parseBlocks } from "../../../worker/domain/blocks.js";
import { DocBlocks } from "./doc-blocks";
import {
  canSaveDoc,
  carriedDraft,
  docCounter,
  docEditor,
  draftProblem,
  keepMineStep,
  lineDiff,
  lineOffset,
  problemLine,
  saveOf,
  TITLE_MAX,
  byPhrase,
  whoPhrase,
  type DocDraft,
  type DocEditor,
} from "./doc-view";
import { WhoAvatar } from "./parts";

/** What the editor's face needs, from a doc's editor or New doc's. */
type EditorState = {
  draft: DocDraft;
  setTitle: (title: string) => void;
  setBody: (body: string) => void;
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  save: () => void;
  discard: () => void;
  /** A refused save's reason (not a conflict, which has its own panel). */
  error: string | null;
  /** The version a save ran into, the one the draft started from, and one that arrived live. */
  conflict: Doc | null;
  base: Doc | null;
  newer: Doc | null;
  keepMine: () => void;
  useTheirs: () => void;
  reload: () => void;
};

/**
 * A doc's editor: the draft against the doc, every fresh copy (a `doc`
 * event's refetch, a save's reply) meeting it through `receive`. A Save that
 * leaves nothing unsaved closes the editor.
 */
function useDocEditor(doc: Doc, onClose: () => void, carried?: DocDraft): EditorState {
  const [stored, setStored] = useState<DocEditor>(() =>
    carried ? docEditor.edit(docEditor.editorFor(doc), carried) : docEditor.editorFor(doc),
  );
  const view = (e: DocEditor) => docEditor.receive(e, doc);
  const editor = view(stored);
  const setEditor = (update: (e: DocEditor) => DocEditor) => setStored((e) => update(view(e)));
  // The editor as last drawn, for a save's reply to see typing that went on meanwhile.
  const latest = useRef(editor);
  useLayoutEffect(() => {
    latest.current = editor;
  });
  const [error, setError] = useState<string | null>(null);
  const save = useSaveDoc(doc.id);

  const send = (from: DocEditor) => {
    const sent = from.draft;
    setError(null);
    save.mutate(saveOf(from), {
      onSuccess: (next) => {
        const after = docEditor.saved(latest.current, next, sent);
        setEditor((e) => docEditor.saved(e, next, sent));
        if (!docEditor.isDirty(after)) onClose();
      },
      onError: (failure) => {
        const refused = refusalOf(failure);
        if (refused?.kind === "conflict") setEditor((e) => docEditor.conflicted(e, refused.doc));
        else setError(refused?.hint ?? errorHint(failure));
      },
    });
  };

  return {
    draft: editor.draft,
    setTitle: (title) => setEditor((e) => docEditor.edit(e, { ...e.draft, title })),
    setBody: (body) => setEditor((e) => docEditor.edit(e, { ...e.draft, body })),
    dirty: docEditor.isDirty(editor),
    canSave: canSaveDoc(editor),
    saving: save.isPending,
    save: () => send(editor),
    discard: () => {
      setError(null);
      setEditor(docEditor.discard);
      onClose();
    },
    error,
    conflict: editor.conflict,
    base: editor.base,
    newer: editor.newer,
    keepMine: () => {
      const { editor: next, save: toSave } = keepMineStep(editor);
      setEditor(() => next);
      // Theirs already is the draft: nothing to save, the editor's work is done.
      if (toSave) send(next);
      else onClose();
    },
    useTheirs: () => setEditor(docEditor.takeTheirs),
    reload: () => setEditor(docEditor.reload),
  };
}

/**
 * New doc's editor (§10.5): the title from the dialog and an empty body.
 * Nothing exists until Save, which makes the doc (linked to the task, from
 * Task detail) and opens it; Discard goes back and leaves nothing behind.
 */
function useNewDocEditor(
  title: string,
  taskId: string | undefined,
  areaId: string | undefined,
  onDiscard: () => void,
) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<DocDraft>({ title, body: "" });
  // The draft as last drawn: typing goes on while the doc is being made.
  const latest = useRef(draft);
  useLayoutEffect(() => {
    latest.current = draft;
  });
  const create = useCreateDoc();
  const problem = draftProblem(draft);
  const state: EditorState = {
    draft,
    setTitle: (t) => setDraft((d) => ({ ...d, title: t })),
    setBody: (body) => setDraft((d) => ({ ...d, body })),
    dirty: true,
    canSave: !problem && !create.isPending,
    saving: create.isPending,
    save: () => {
      const sent = { title: draft.title.trim(), body: draft.body };
      create.mutate(
        { ...sent, ...(taskId ? { task_id: taskId } : {}), ...(areaId ? { area_id: areaId } : {}) },
        {
          // Typed while it was made: the doc's editor opens on it, unsaved.
          onSuccess: (doc) =>
            void navigate(`/docs/${doc.id}`, {
              replace: true,
              state: { draft: carriedDraft(sent, latest.current) },
            }),
        },
      );
    },
    discard: onDiscard,
    error: create.error ? errorHint(create.error) : null,
    conflict: null,
    base: null,
    newer: null,
    keepMine: () => undefined,
    useTheirs: () => undefined,
    reload: () => undefined,
  };
  return state;
}

// ── Pieces ──────────────────────────────────────────────────────────────────

/** "The Chief of Staff changed this doc while you were editing." with Reload (48b). */
function ChangedBanner({ newer, me, onReload }: { newer: Doc; me: Me; onReload: () => void }) {
  return (
    <div
      role="status"
      data-doc-changed
      className="flex items-center gap-3 rounded-tile bg-secondary px-4 py-2.5 text-sm text-copy"
    >
      <RefreshCw aria-hidden className="size-4 shrink-0 text-copper" />
      <span className="min-w-0 flex-1">
        {whoPhrase(newer.updated_by, me)} changed this doc while you were editing.
      </span>
      <Button size="xs" onClick={onReload}>
        Reload
      </Button>
    </div>
  );
}

/**
 * 48c: the save wasn't made. Their change (the lines it added and removed
 * against the version this draft started from), Use theirs, or Keep mine,
 * which saves over it for good.
 */
function ConflictPanel({ state, me }: { state: EditorState; me: Me }) {
  const theirs = state.conflict;
  if (!theirs || !state.base) return null;
  const who = whoPhrase(theirs.updated_by, me);
  const diff = lineDiff(state.base.body, theirs.body);
  const renamed = theirs.title !== state.base.title;
  return (
    <div
      role="alert"
      data-doc-conflict
      className="flex flex-col gap-3 rounded-panel bg-card px-4.5 py-4 shadow-[0_0_0_1px_rgb(162_59_47/25%)]"
    >
      <div className="flex flex-col gap-1">
        <p className="text-body font-semibold text-foreground">
          Not saved: this doc changed while you were editing
        </p>
        <p className="text-sm text-muted-foreground">
          {who} saved a change at {shortTime(theirs.updated_at, me.workspace.timezone)}. Your edit
          is still in the editor below.
        </p>
      </div>
      {renamed || diff.lines.length > 0 ? (
        <div
          data-their-change
          className="flex flex-col gap-0.5 rounded-md bg-background/60 px-3 py-2.5"
        >
          <span className="pb-1 text-xs text-hint">Their change</span>
          {renamed ? <DiffRow sign="+" text={`Title: ${theirs.title}`} /> : null}
          {diff.lines.map((line, i) => (
            <DiffRow key={i} sign={line.sign} text={line.text} />
          ))}
          {diff.more > 0 ? (
            <span className="pt-1 text-xs text-hint">
              and {diff.more} more {diff.more === 1 ? "line" : "lines"}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={state.saving} onClick={state.useTheirs}>
          Use theirs
        </Button>
        <Button size="sm" variant="outline" disabled={state.saving} onClick={state.keepMine}>
          Keep mine
        </Button>
        <span className="px-1 text-meta text-hint">
          Keep mine saves over their change, and it can't be put back.
        </span>
      </div>
    </div>
  );
}

function DiffRow({ sign, text }: { sign: "+" | "-"; text: string }) {
  return (
    <span
      data-diff={sign === "+" ? "added" : "removed"}
      className={cn(
        "rounded-[4px] px-1.5 font-mono text-xs leading-5 break-words whitespace-pre-wrap",
        sign === "+"
          ? "bg-moss/12 text-[#2E5039]"
          : "bg-brick/10 text-brick line-through decoration-brick/40",
      )}
    >
      {sign} {text}
    </span>
  );
}

function Counter({ length }: { length: number }) {
  const c = docCounter(length);
  return (
    <span data-counter className={cn("text-xs tabular-nums", toneText[c.tone] ?? "text-hint")}>
      {c.text}
    </span>
  );
}

/**
 * Write and Preview's tab, the text area, and the draft's broken blocks
 * (slice 2g spec §9), read as the person types; a problem's click puts the
 * cursor on its line, in Write.
 */
type Writing = {
  tab: string;
  setTab: (tab: string) => void;
  area: RefObject<HTMLTextAreaElement | null>;
  problems: ReturnType<typeof brokenBlocks>;
  goTo: (line: number) => void;
};

/** The editor's line height (`leading-[22px]`), to scroll a problem's line into view. */
const LINE_PX = 22;

function useWriting(body: string): Writing {
  const [tab, setTab] = useState("write");
  const area = useRef<HTMLTextAreaElement>(null);
  const read = useDeferredValue(body);
  const problems = useMemo(() => brokenBlocks(parseBlocks(read)), [read]);
  const goTo = (line: number) => {
    setTab("write");
    requestAnimationFrame(() => {
      const el = area.current;
      if (!el) return;
      const at = lineOffset(el.value, line);
      el.focus();
      el.setSelectionRange(at, at);
      el.scrollTop = Math.max(0, (line - 1) * LINE_PX - el.clientHeight / 3);
    });
  };
  return { tab, setTab, area, problems, goTo };
}

/**
 * The draft's broken blocks (55, 55b): a line each, which puts the cursor
 * on it. Save is never blocked by one.
 */
function BlockProblems({
  problems,
  goTo,
  boxed,
}: Pick<Writing, "problems" | "goTo"> & { boxed?: boolean }) {
  if (problems.length === 0) return null;
  return (
    <div
      data-doc-problems
      className={cn(
        "flex shrink-0 flex-col gap-1 bg-brick-tint",
        boxed
          ? "max-h-32 overflow-y-auto rounded-tile px-3 py-2.5 shadow-[0_0_0_1px] shadow-brick/15"
          : "max-h-40 overflow-y-auto border-t border-foreground/[0.08] px-6 py-2.5",
      )}
    >
      {problems.map((p) => (
        <button
          key={`${p.line}:${p.type}`}
          type="button"
          data-problem-line={p.line}
          onClick={() => goTo(p.line)}
          className="flex items-start gap-2 text-left text-meta leading-[18px] text-brick hover:underline"
        >
          <CircleAlert aria-hidden strokeWidth={2} className="mt-0.5 size-3.5 shrink-0" />
          {problemLine(p)}
        </button>
      ))}
      {boxed ? null : (
        <p className="pl-[22px] text-xs leading-4 text-hint">
          It saves as it is and shows as a block that couldn't be drawn.
        </p>
      )}
    </div>
  );
}

/** Write (the markdown) and Preview (as the page reads it), the counter at the right. */
function WriteAndPreview({
  state,
  tab,
  setTab,
  area,
  className,
  areaClassName,
}: Pick<Writing, "tab" | "setTab" | "area"> & {
  state: EditorState;
  className?: string;
  areaClassName?: string;
}) {
  const problem = draftProblem(state.draft);
  return (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(String(v))}
      className={cn("min-h-0 gap-0", className)}
    >
      <div className="flex items-center justify-between gap-3 border-b border-foreground/[0.08] px-2.5 py-2">
        <TabsList>
          <TabsTrigger value="write" className="px-2.5">
            Write
          </TabsTrigger>
          <TabsTrigger value="preview" className="px-2.5">
            Preview
          </TabsTrigger>
        </TabsList>
        <Counter length={state.draft.body.length} />
      </div>
      <TabsContent value="write" className="flex min-h-0 flex-1 flex-col">
        <Textarea
          ref={area}
          aria-label="The doc, in markdown"
          value={state.draft.body}
          spellCheck
          onChange={(e) => state.setBody(e.target.value)}
          aria-invalid={problem && state.draft.body.length > 0 ? true : undefined}
          placeholder="Start writing. Markdown works: ## for a heading, - for a list."
          className={cn(
            "min-h-0 flex-1 resize-none rounded-none border-0 bg-transparent px-6 py-5 font-mono text-[13px] leading-[22px] shadow-none [field-sizing:fixed] focus-visible:ring-0 md:text-[13px]",
            areaClassName,
          )}
        />
      </TabsContent>
      <TabsContent value="preview" className="min-h-0 flex-1 overflow-y-auto">
        <div data-doc-preview className={cn("px-6 py-5 md:px-12 md:pt-7 md:pb-8", areaClassName)}>
          {state.draft.body.trim() ? (
            <DocBlocks body={state.draft.body} />
          ) : (
            <p className="text-body text-muted-foreground">Nothing here yet.</p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}

function TitleInput({ state, className }: { state: EditorState; className?: string }) {
  return (
    <input
      aria-label="Title"
      data-doc-title-input
      value={state.draft.title}
      maxLength={TITLE_MAX}
      onChange={(e) => state.setTitle(e.target.value)}
      className={cn(
        "w-full min-w-0 rounded-panel border border-input bg-card px-3.5 font-display tracking-tight text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
        className,
      )}
    />
  );
}

/** Why it can't save: over a limit, no title, or a refused save. */
function Problems({ state }: { state: EditorState }) {
  const problem = draftProblem(state.draft);
  return (
    <>
      {problem && (state.dirty || state.base === null) ? <FieldError>{problem}</FieldError> : null}
      {state.error ? <FieldError data-doc-save-error>{state.error}</FieldError> : null}
    </>
  );
}

function SaveButton({ state, className }: { state: EditorState; className?: string }) {
  return (
    <Button
      data-doc-save
      disabled={!state.canSave || state.saving}
      onClick={state.save}
      className={className}
    >
      {state.saving ? <Spinner data-icon="inline-start" /> : null}
      Save
    </Button>
  );
}

// ── The editor at 1440 and on a phone ───────────────────────────────────────

type Face = {
  state: EditorState;
  me: Me;
  /** "Changed by Chief of Staff · 9:42 am", or New doc's line. */
  meta: ReactNode;
  /** The footer's word on the draft: "Unsaved changes", "New doc, not saved yet". */
  status: string;
};

/** 48b, 48c, 51e: the title, the banner or the conflict, and the editor in a card. */
function DesktopEditor({ state, me, meta, status }: Face) {
  const { tab, setTab, area, problems, goTo } = useWriting(state.draft.body);
  return (
    <main
      data-doc-editor
      className="flex w-full flex-col items-center px-4 pt-6 pb-16 sm:px-6 md:px-12 md:pt-9"
    >
      <div className="flex w-full max-w-letter flex-col gap-3">
        <Link href="/docs" className="w-fit text-meta text-muted-foreground hover:text-foreground">
          Docs
        </Link>
        <TitleInput state={state} className="h-14 text-[32px] leading-10" />
        {meta}
        <div className="mt-3 flex flex-col gap-5">
          {state.newer && !state.conflict ? (
            <ChangedBanner newer={state.newer} me={me} onReload={state.reload} />
          ) : null}
          <ConflictPanel state={state} me={me} />
          <div className="flex flex-col overflow-hidden rounded-panel bg-card shadow-[0_0_0_1px_var(--color-border),0_8px_24px_rgb(31_42_68/4%)]">
            <WriteAndPreview
              state={state}
              tab={tab}
              setTab={setTab}
              area={area}
              className="flex h-[max(24rem,calc(100dvh-26rem))] flex-col"
            />
            <BlockProblems problems={problems} goTo={goTo} />
            <div className="flex flex-wrap items-center gap-3 border-t border-foreground/[0.06] px-6 py-2.5">
              <span className="min-w-0 flex-1 text-meta text-muted-foreground">{status}</span>
              <Button variant="outline" onClick={state.discard} disabled={state.saving}>
                Discard
              </Button>
              <SaveButton state={state} />
            </div>
          </div>
          <Problems state={state} />
        </div>
      </div>
    </main>
  );
}

/**
 * 48g: full screen without the tab bar (over it, under the top bar): Discard
 * and Save at the top, the title, Write and Preview with the counter, and the
 * editor filling the rest.
 */
function PhoneEditor({ state, me }: Face) {
  const { tab, setTab, area, problems, goTo } = useWriting(state.draft.body);
  return (
    <div
      data-doc-editor
      className="fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top))] bottom-0 z-40 flex flex-col gap-3 bg-background px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          className="-ml-2 px-2 text-base"
          onClick={state.discard}
          disabled={state.saving}
        >
          Discard
        </Button>
        <span className="text-sm text-muted-foreground">Editing</span>
        <SaveButton state={state} className="h-10 px-5 text-base" />
      </div>
      <TitleInput state={state} className="h-[52px] shrink-0 text-[26px] leading-8" />
      {state.newer && !state.conflict ? (
        <ChangedBanner newer={state.newer} me={me} onReload={state.reload} />
      ) : null}
      <div className="max-h-[45%] shrink-0 overflow-y-auto empty:hidden">
        <ConflictPanel state={state} me={me} />
      </div>
      <Problems state={state} />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-panel border border-foreground bg-card">
        <WriteAndPreview
          state={state}
          tab={tab}
          setTab={setTab}
          area={area}
          className="flex flex-1 flex-col"
        />
      </div>
      <BlockProblems problems={problems} goTo={goTo} boxed />
    </div>
  );
}

function EditorFace(face: Face) {
  const mobile = useIsMobile();
  return mobile ? <PhoneEditor {...face} /> : <DesktopEditor {...face} />;
}

/** A doc's editor (48b), closed by a Save that leaves nothing unsaved, or by Discard. */
export function DocEditView({
  doc,
  me,
  onClose,
  carried,
}: {
  doc: Doc;
  me: Me;
  onClose: () => void;
  /** New doc's typing during its first Save, still to save. */
  carried?: DocDraft;
}) {
  const state = useDocEditor(doc, onClose, carried);
  const meta = (
    <span className="flex items-center gap-2 text-meta text-muted-foreground">
      <WhoAvatar who={doc.updated_by} size={20} />
      Changed by {byPhrase(doc.updated_by, me)} · {shortTime(doc.updated_at, me.workspace.timezone)}
    </span>
  );
  return (
    <EditorFace
      state={state}
      me={me}
      meta={meta}
      status={state.dirty ? "Unsaved changes" : "No changes yet"}
    />
  );
}

/** New doc's editor (51e): nothing's made until Save. */
export function NewDocEditView({
  title,
  taskId,
  areaId,
  me,
  onDiscard,
}: {
  title: string;
  taskId: string | undefined;
  /** Slice 6d (spec §4.2): made from Docs filtered to an Area, it's filed there. */
  areaId?: string;
  me: Me;
  onDiscard: () => void;
}) {
  const state = useNewDocEditor(title, taskId, areaId, onDiscard);
  const meta = (
    <span className="flex items-center gap-2 text-meta text-muted-foreground">
      <WhoAvatar
        who={{
          kind: "person",
          id: me.user.id,
          name: me.user.name,
          initials: me.user.initials,
          tint: me.user.tint,
        }}
        size={20}
      />
      New doc by you · not saved yet
    </span>
  );
  return (
    <EditorFace
      state={state}
      me={me}
      meta={meta}
      status="Not saved yet: Save makes the doc, Discard leaves nothing behind"
    />
  );
}
